import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../../hooks/useAuth";
import {
  useProviderOwnerOnboardingStatus,
  useSubmitProviderOnboardingForReview,
  canSubmitOnboardingForReview,
} from "../../provider-owner.hooks";

const cardStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 18,
  padding: "1rem",
};

const sectionGridStyle: React.CSSProperties = {
  display: "grid",
  gap: "1rem",
};

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: "0.75rem",
  flexWrap: "wrap",
  alignItems: "center",
};

const checklistGridStyle: React.CSSProperties = {
  display: "grid",
  gap: "0.75rem",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
};

const checklistItemStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  gap: "0.75rem",
  alignItems: "flex-start",
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  padding: "0.9rem 1rem",
};

const badgeBaseStyle: React.CSSProperties = {
  borderRadius: 999,
  padding: "0.35rem 0.8rem",
  fontSize: "0.82rem",
  fontWeight: 600,
};

const badgeCompleteStyle: React.CSSProperties = {
  ...badgeBaseStyle,
  background: "#dcfce7",
  color: "#166534",
};

const badgeMissingStyle: React.CSSProperties = {
  ...badgeBaseStyle,
  background: "#fef2f2",
  color: "#991b1b",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  color: "#0f172a",
};

const sectionSubtitleStyle: React.CSSProperties = {
  margin: "0.35rem 0 0",
  color: "#64748b",
  fontSize: "0.94rem",
  lineHeight: 1.6,
};

const primaryButtonStyle: React.CSSProperties = {
  border: "none",
  borderRadius: 12,
  background: "#111827",
  color: "#ffffff",
  padding: "0.9rem 1.1rem",
  fontWeight: 700,
  cursor: "pointer",
};

const primaryButtonDisabledStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  opacity: 0.5,
  cursor: "not-allowed",
};

const secondaryButtonStyle: React.CSSProperties = {
  textDecoration: "none",
  border: "1px solid #d1d5db",
  borderRadius: 12,
  padding: "0.9rem 1.1rem",
  color: "#111827",
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const statusChipStyle: React.CSSProperties = {
  borderRadius: 999,
  padding: "0.35rem 0.8rem",
  fontSize: "0.82rem",
  fontWeight: 600,
  background: "#eff6ff",
  color: "#1d4ed8",
};

export default function ProviderReviewSubmitPage() {
  const { user } = useAuth();
  const providerId = user?.providerId ?? "";

  const statusQuery = useProviderOwnerOnboardingStatus(providerId);
  const submitForReview = useSubmitProviderOnboardingForReview();

  if (!providerId) {
    return (
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>Review and submit onboarding</h2>
        <p style={{ marginTop: "0.5rem", color: "#b91c1c" }}>
          No provider is linked to this account yet. Please contact support.
        </p>
      </div>
    );
  }

  if (statusQuery.isLoading) {
    return (
      <div style={cardStyle}>
        <p style={{ margin: 0, color: "#475569" }}>Loading onboarding status...</p>
      </div>
    );
  }

  if (statusQuery.isError || !statusQuery.data) {
    return (
      <div style={cardStyle}>
        <p style={{ margin: 0, color: "#b91c1c" }}>
          Unable to load onboarding status. Please refresh and try again.
        </p>
      </div>
    );
  }

  const status = statusQuery.data;

  const profileComplete = Boolean(status.profileComplete);
  const verificationComplete = Boolean(status.verificationComplete);
  const servicesComplete = Boolean(status.servicesComplete);
  const calendarComplete = Boolean(status.calendarComplete);
  const depositPolicyComplete = Boolean(status.depositPolicyComplete);
  const portfolioComplete = Boolean(status.portfolioComplete);
  const documentsComplete = Boolean(status.documentsComplete);

  const completenessFlags = [
    {
      key: "profile",
      title: "Business profile",
      description:
        "Core business details, contact information, location, and category alignment.",
      complete: profileComplete,
      linkTo: "/provider/onboarding/profile",
    },
    {
      key: "verification",
      title: "Verification and KYC",
      description:
        "Legal documents, identity verification, and compliance-related declarations.",
      complete: verificationComplete,
      linkTo: "/provider/onboarding/verification",
    },
    {
      key: "services",
      title: "Services and pricing",
      description:
        "Taxonomy-aligned services with duration, pricing, and deposit rules.",
      complete: servicesComplete,
      linkTo: "/provider/onboarding/services",
    },
    {
      key: "calendar",
      title: "Calendar and availability",
      description:
        "Operating hours, slot templates, and basic availability for bookings.",
      complete: calendarComplete,
      linkTo: "/provider/onboarding/calendar",
    },
    {
      key: "depositPolicy",
      title: "Deposit and payment policy",
      description:
        "Cancellation, rescheduling, and deposit rules for operational control.",
      complete: depositPolicyComplete,
      linkTo: "/provider/onboarding/deposit-policy",
    },
    {
      key: "portfolio",
      title: "Media and portfolio",
      description:
        "Photos and portfolio content that help BDOs and customers evaluate quality.",
      complete: portfolioComplete,
      linkTo: "/provider/onboarding/portfolio",
    },
    {
      key: "documents",
      title: "Additional documents",
      description:
        "Licences, certificates, and supporting documents where applicable.",
      complete: documentsComplete,
      linkTo: "/provider/onboarding/verification",
    },
  ];

  const canSubmit = canSubmitOnboardingForReview(status);
  const disableSubmitButton = !canSubmit || submitForReview.isPending;

  return (
    <div style={sectionGridStyle}>
      <section style={cardStyle}>
        <div style={headerRowStyle}>
          <div>
            <h2 style={sectionTitleStyle}>Review and submit onboarding</h2>
            <p style={sectionSubtitleStyle}>
              Check your onboarding checklist, finish any missing sections, and submit
              for BDO and operations review.
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
            <span style={statusChipStyle}>
              Status: {status.onboardingStatus ?? "UNKNOWN"}
            </span>
            <span style={{ fontSize: "0.85rem", color: "#64748b" }}>
              Completeness: {status.completeness ?? 0}%
              {" · "}
              Missing items: {status.missingItemsCount ?? 0}
            </span>
          </div>
        </div>
      </section>

      <section style={cardStyle}>
        <h3 style={sectionTitleStyle}>Checklist</h3>
        <p style={sectionSubtitleStyle}>
          Every section should be complete before you submit for review. You can still
          make changes later if BDO or operations return your application with
          feedback.
        </p>

        <div style={{ marginTop: "1rem", ...checklistGridStyle }}>
          {completenessFlags.map((item) => (
            <div key={item.key} style={checklistItemStyle}>
              <div>
                <span
                  style={item.complete ? badgeCompleteStyle : badgeMissingStyle}
                >
                  {item.complete ? "Complete" : "Missing"}
                </span>
              </div>
              <div style={{ display: "grid", gap: "0.3rem" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "0.5rem",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontWeight: 600, color: "#0f172a" }}>
                    {item.title}
                  </span>
                  <Link to={item.linkTo} style={secondaryButtonStyle}>
                    Review
                  </Link>
                </div>
                <p
                  style={{
                    margin: 0,
                    color: "#64748b",
                    fontSize: "0.92rem",
                    lineHeight: 1.6,
                  }}
                >
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section style={cardStyle}>
        <div style={headerRowStyle}>
          <div>
            <h3 style={sectionTitleStyle}>Submit for review</h3>
            <p style={sectionSubtitleStyle}>
              When you submit, your onboarding will move into the BDO and operations
              queues. They may approve, return with comments, or request additional
              information.
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            <button
              type="button"
              style={disableSubmitButton ? primaryButtonDisabledStyle : primaryButtonStyle}
              disabled={disableSubmitButton}
              onClick={() => submitForReview.mutate(providerId)}
            >
              {submitForReview.isPending ? "Submitting..." : "Submit for review"}
            </button>
            <Link to="/provider/onboarding/start" style={secondaryButtonStyle}>
              Back to onboarding home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}